import { useEffect, useState } from 'react';
import { camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  DatePickerInput,
  Form,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
  SelectInput,
  TextAreaInput,
  TextInput,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { CommunicationChannelsEnum } from '@thinknet-gmbh/platform-common';

import { BooleanSelectInput } from 'src/pages/employee/components/boolean-select-input';
import { useGetAllEmployeesForList } from 'src/hooks/employees/get-all-employees-for-list.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { filterOption } from 'src/common/helpers/common.helpers';
import { StyledRateInput } from 'src/pages/vacancies/sub-pages/vacancy-details/components/styled-rate-input';

import { IInterviewFormItemsProps } from './interview-form-items.interfaces';
import {
  InterviewFormWrapper,
  FormContentWrapper,
  DeleteContainer,
} from './interview-form-items.styles';

const InterviewFormItems = ({
  title,
  feedbackLabel,
  parentField,
  actions,
  isAddedAdditionalInterview,
  hideActionButtons,
  fields,
}: IInterviewFormItemsProps) => {
  const [trigger, setTrigger] = useState(true);
  const { t, i18n } = useTranslation(['common', 'vacancies']);
  const currentCompanyId = useGetCurrentCompanyId();
  const { employees } = useGetAllEmployeesForList({
    filter: { companyId: currentCompanyId, accountActive: true },
  });

  useEffect(() => {
    if (isAddedAdditionalInterview && actions && trigger) {
      setTrigger(false);
      actions.add([{}]);
    }
  }, [actions, isAddedAdditionalInterview, trigger]);

  return (
    <InterviewFormWrapper>
      <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
        {title}
      </Typography>
      <FormContentWrapper>
        <div>
          <DeleteContainer>
            <Form.Item
              name={[parentField, 'interviewName']}
              label={t('common:labels.label')}
              rules={[{ required: true, message: t('vacancies:validation.interviewNameRequired') }]}
            >
              <TextInput placeholder={t('vacancies:labels.writeInterviewName')} />
            </Form.Item>
            <Icon
              icon={ICONS.trash}
              rightMargin={false}
              color={PLATFORM_THEME_COLORS.tone.red}
              size={22}
              onClick={() => {
                actions?.remove(Number(parentField));
                if (hideActionButtons && fields.length < 2) {
                  hideActionButtons();
                }
              }}
            />
          </DeleteContainer>
          <Form.Item
            name={[parentField, 'interviewSet']}
            label={t('vacancies:labels.interviewSet')}
            rules={[{ required: true, message: t('vacancies:validation.interviewSetRequired') }]}
          >
            <BooleanSelectInput t={t} />
          </Form.Item>
          <Form.Item name={[parentField, 'feedback']} label={feedbackLabel}>
            <TextAreaInput
              showCount
              maxLength={3000}
              placeholder={t('vacancies:labels.writeFeedback')}
            />
          </Form.Item>
        </div>

        <div>
          <Form.Item
            name={[parentField, 'date']}
            label={t('common:labels.date')}
            rules={[{ required: true, message: t('employee:validationError.dateRequired') }]}
          >
            <DatePickerInput lang={i18n.language} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item
            name={[parentField, 'interviewerId']}
            label={t('vacancies:labels.interviewer')}
            rules={[{ required: true, message: t('vacancies:validation.interviewerRequired') }]}
          >
            <SelectInput
              placeholder={t('vacancies:labels.interviewer')}
              showSearch
              filterOption={filterOption}
            >
              {employees.map(({ _id, profile }: any) => (
                <SelectInput.Option value={_id} key={_id}>
                  {profile.displayName}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
        </div>
        <div>
          <Form.Item
            name={[parentField, 'communicationChannel']}
            label={t('vacancies:labels.communicationChannel')}
            rules={[{ required: true, message: t('vacancies:validation.communicationRequired') }]}
          >
            <SelectInput placeholder={t('vacancies:labels.mobilePhone')}>
              {Object.keys(CommunicationChannelsEnum).map(channel => (
                <SelectInput.Option key={channel} value={channel}>
                  {t(`vacancies:labels.${camelCase(channel)}`)}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue(['interviews', parentField, 'communicationChannel']) ===
                CommunicationChannelsEnum.ONLINE && (
                <Form.Item
                  name={[parentField, 'eventLink']}
                  label={t('vacancies:labels.linkToMeeting')}
                  rules={[
                    {
                      required: true,
                      message: t('vacancies:validation.meetingLinkRequired'),
                    },
                  ]}
                >
                  <TextInput placeholder={t('vacancies:labels.putTheLink')} />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item name={[parentField, 'rating']} label={t('vacancies:labels.rating')}>
            <StyledRateInput />
          </Form.Item>
        </div>
      </FormContentWrapper>
    </InterviewFormWrapper>
  );
};

export default InterviewFormItems;
